import { useState, useEffect } from "react";
import { Spinner } from "./PageLoader";
import PhoneField from "./PhoneField";
import RichText from "./RichText";

/**
 * Shared auth modal: Join (register) / Sign in / Forgot password.
 * Used by both the community landing and the group feed (no page redirect).
 */
function AuthModal({
  mode,
  community,
  primary,
  form,
  setForm,
  busy,
  msg,
  forgotStep = 1,
  onClose,
  onSwitch,
  onSubmitJoin,
  onSubmitSignin,
  onSubmitForgot,
}) {
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Lock the background page scroll while the modal is open (notably on mobile,
  // where the page behind the overlay would otherwise still scroll).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Privacy/Terms viewer ("privacy" | "terms" | null) for the policy links.
  const [policyView, setPolicyView] = useState(null);
  const policies = community?.policies || {};
  const gdpr = community?.gdpr || {};
  const showConsent = mode === "join" && Number(gdpr.enabled) === 1;
  const hasPrivacy = !!policies.privacy;
  const hasTerms = !!policies.terms;

  // Forgot password is a 3-step OTP flow: 1) email → 2) code → 3) new password.
  const forgotSub = {
    1: "Enter your email and we'll send you a 6-digit code.",
    2: "Enter the 6-digit code we just emailed you.",
    3: "Choose a new password for your account.",
  }[forgotStep];

  const heading = {
    join: { title: `Join ${community?.name}`, sub: "Create your free account to access groups, posts & rewards." },
    signin: { title: "Welcome back", sub: `Sign in to ${community?.name}` },
    forgot: { title: "Reset your password", sub: forgotSub },
  }[mode];

  const success = msg.type === "success";

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-gray-900/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[94vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Branded header: community banner in the background, tinted with the
            selected theme colour so the text on top stays readable. */}
        <div className="relative px-6 pt-8 pb-7 text-center overflow-hidden">
          {community?.banner && (
            <div
              aria-hidden="true"
              className="absolute inset-0 z-0 bg-center bg-cover"
              style={{ backgroundImage: `url("${community.banner}")` }}
            />
          )}
          <div
            aria-hidden="true"
            className="absolute inset-0 z-0"
            style={{
              background: "linear-gradient(160deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.70) 100%)",
            }}
          />
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3.5 right-3.5 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/35 text-white text-lg leading-none transition"
          >
            ×
          </button>
          <div className="relative z-10">
            <h3
              className="text-white text-xl font-bold"
              style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}
            >
              {heading.title}
            </h3>
            <p
              className="text-white/95 text-sm mt-1.5 leading-relaxed"
              style={{ textShadow: "0 1px 3px rgba(0,0,0,0.4)" }}
            >
              {heading.sub}
            </p>
          </div>
        </div>

        {/* Success screen */}
        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ backgroundColor: `${primary}1A` }}>
              <svg className="w-8 h-8" style={{ color: primary }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h4 className="text-lg font-bold text-gray-900 mt-4">
              {mode === "join" ? "You're in! 🎉" : mode === "signin" ? "Signed in" : "Password updated"}
            </h4>
            <p className="text-sm text-gray-500 mt-1.5">{msg.text}</p>
            <button
              onClick={onClose}
              style={{ backgroundColor: primary }}
              className="w-full text-white font-semibold py-3 rounded-xl mt-6 hover:opacity-90 transition"
            >
              Continue
            </button>
          </div>
        ) : (
          <form
            onSubmit={mode === "join" ? onSubmitJoin : mode === "signin" ? onSubmitSignin : onSubmitForgot}
            className="p-6 space-y-4"
          >
            {msg.type === "error" && msg.text && (
              <div className="flex items-start gap-2 text-sm rounded-xl px-3.5 py-2.5 bg-red-50 text-red-600 border border-red-100">
                <svg className="w-4 h-4 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 5a1 1 0 112 0v5a1 1 0 11-2 0V5zm1 9a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                </svg>
                {msg.text}
              </div>
            )}

            {mode === "join" && (
              <Field label="Full name" placeholder="Jane Doe" value={form.name} onChange={set("name")} required disabled={busy} primary={primary} icon={ICONS.user} />
            )}

            {/* Email — for join/signin, and the first step of forgot password.
                On later forgot steps it stays visible but read-only as a reminder. */}
            {(mode !== "forgot" || forgotStep === 1) && (
              <Field label="Email address" type="email" placeholder="you@example.com" value={form.email} onChange={set("email")} required disabled={busy} primary={primary} icon={ICONS.mail} />
            )}

            {mode === "join" && (
              <PhoneField
                value={form.phone}
                onChange={set("phone")}
                disabled={busy}
                primary={primary}
                optional={Number(community?.phone_required) !== 1}
                defaultCountry={community?.default_country || "IN"}
              />
            )}

            {/* Forgot step 2 — the 6-digit code */}
            {mode === "forgot" && forgotStep === 2 && (
              <>
                <p className="-mt-1 text-[13px] text-gray-500">
                  Code sent to <span className="font-semibold text-gray-700">{form.email}</span>.
                </p>
                <Field
                  label="6-digit code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="••••••"
                  value={form.otp}
                  onChange={(v) => set("otp")(v.replace(/\D/g, "").slice(0, 6))}
                  required
                  disabled={busy}
                  primary={primary}
                  icon={ICONS.lock}
                />
              </>
            )}

            {/* Forgot step 3 — the new password + confirmation */}
            {mode === "forgot" && forgotStep === 3 && (
              <>
                <Field label="New password" type="password" placeholder="Create a new password" value={form.password} onChange={set("password")} required disabled={busy} primary={primary} icon={ICONS.lock} />
                <Field label="Confirm password" type="password" placeholder="Re-enter your new password" value={form.password_confirmation} onChange={set("password_confirmation")} required disabled={busy} primary={primary} icon={ICONS.lock} />
              </>
            )}

            {mode !== "forgot" && (
              <div>
                <Field
                  label="Password"
                  type="password"
                  placeholder={mode === "join" ? "Create a password" : "Your password"}
                  value={form.password}
                  onChange={set("password")}
                  required
                  disabled={busy}
                  primary={primary}
                  icon={ICONS.lock}
                />
                {mode === "signin" && (
                  <div className="text-right mt-1.5">
                    <button
                      type="button"
                      onClick={() => onSwitch("forgot")}
                      className="text-xs font-medium hover:underline"
                      style={{ color: primary }}
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              style={{ backgroundColor: primary }}
              className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl hover:opacity-90 transition disabled:opacity-60 shadow-sm"
            >
              {busy && (
                <Spinner size={16} />
              )}
              {busy
                ? "Please wait…"
                : mode === "join"
                ? "Join now"
                : mode === "signin"
                ? "Sign in"
                : forgotStep === 1
                ? "Send code"
                : forgotStep === 2
                ? "Verify code"
                : "Reset password"}
            </button>

            {/* GDPR consent checkbox (shown on signup when enabled). */}
            {showConsent && (
              <label className="flex items-start gap-2.5 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={!!form.gdpr_consent}
                  disabled={busy}
                  onChange={(e) => set("gdpr_consent")(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300"
                  style={{ accentColor: primary }}
                />
                <span>By Creating an account, I accept the Privacy and Terms{Number(gdpr.mandatory) === 1 && <span className="text-red-500"> *</span>}</span>
              </label>
            )}

            <div className="text-center text-sm text-gray-500 pt-1">
              {mode === "join" && (
                <>
                  Already have an account?{" "}
                  <SwitchLink onClick={() => onSwitch("signin")} primary={primary}>Sign in</SwitchLink>
                </>
              )}
              {mode === "signin" && (
                <>
                  New here?{" "}
                  <SwitchLink onClick={() => onSwitch("join")} primary={primary}>Join the community</SwitchLink>
                </>
              )}
              {mode === "forgot" && (
                <SwitchLink onClick={() => onSwitch("signin")} primary={primary}>← Back to sign in</SwitchLink>
              )}
            </div>

            {/* Privacy Policy / Terms links. */}
            {(hasPrivacy || hasTerms) && (
              <div className="flex items-center justify-center gap-3 text-sm font-semibold">
                {hasPrivacy && (
                  <button type="button" onClick={() => setPolicyView("privacy")} className="hover:underline" style={{ color: primary }}>
                    Privacy Policy
                  </button>
                )}
                {hasPrivacy && hasTerms && <span className="text-gray-300">·</span>}
                {hasTerms && (
                  <button type="button" onClick={() => setPolicyView("terms")} className="hover:underline" style={{ color: primary }}>
                    Terms
                  </button>
                )}
              </div>
            )}
          </form>
        )}
      </div>

      {/* Policy viewer overlay (Privacy / Terms content). */}
      {policyView && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-gray-900/60 backdrop-blur-sm p-0 sm:p-4"
          onClick={(e) => { e.stopPropagation(); setPolicyView(null); }}
        >
          <div
            className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="font-semibold text-gray-900">{policyView === "privacy" ? "Privacy Policy" : "Terms"}</h3>
              <button onClick={() => setPolicyView(null)} aria-label="Close" className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
            </div>
            <RichText
              html={policyView === "privacy" ? policies.privacy : policies.terms}
              className="px-6 py-5 overflow-y-auto text-sm text-gray-600 leading-relaxed"
            />
            <div className="px-6 py-3 border-t text-right">
              <button onClick={() => setPolicyView(null)} style={{ backgroundColor: primary }} className="text-white text-sm font-semibold px-5 py-2 rounded-lg hover:opacity-90">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SwitchLink({ onClick, primary, children }) {
  return (
    <button type="button" onClick={onClick} className="font-semibold hover:underline" style={{ color: primary }}>
      {children}
    </button>
  );
}

function Field({ label, value, onChange, type = "text", required, optional, disabled, placeholder, icon, primary = "#0274F7", inputMode, maxLength }) {
  const [focused, setFocused] = useState(false);
  const [reveal, setReveal] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword ? (reveal ? "text" : "password") : type;
  return (
    <label className="block">
      <span className="text-[13px] font-medium text-gray-600 mb-1.5 flex items-center gap-1">
        {label}
        {optional && <span className="text-gray-400 font-normal">(optional)</span>}
      </span>
      <div
        className="flex items-center gap-2.5 rounded-xl border px-3.5 transition-colors"
        style={{
          borderColor: focused ? primary : "#e5e7eb",
          boxShadow: focused ? `0 0 0 3px ${primary}22` : "none",
          backgroundColor: focused ? "#fff" : "#f9fafb",
        }}
      >
        {icon && (
          <span className="shrink-0" style={{ color: focused ? primary : "#9ca3af" }}>
            {icon}
          </span>
        )}
        <input
          type={inputType}
          value={value}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          inputMode={inputMode}
          maxLength={maxLength}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border-0 appearance-none py-2.5 text-sm text-gray-800 outline-none focus:outline-none focus:ring-0 shadow-none placeholder-gray-400"
        />
        {isPassword && value && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setReveal((v) => !v)}
            aria-label={reveal ? "Hide password" : "Show password"}
            className="shrink-0 -mr-1 p-1 transition-colors"
            style={{ color: focused ? primary : "#9ca3af" }}
          >
            {reveal ? ICONS.eyeOff : ICONS.eye}
          </button>
        )}
      </div>
    </label>
  );
}

const ICONS = {
  user: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  mail: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l9 6 9-6M3 7a1 1 0 011-1h16a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V7z" />
    </svg>
  ),
  phone: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2.5a1 1 0 011 .8l1 4a1 1 0 01-.3 1L8 10a12 12 0 006 6l1.2-1.2a1 1 0 011-.3l4 1a1 1 0 01.8 1V19a2 2 0 01-2 2A16 16 0 013 5z" />
    </svg>
  ),
  lock: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V8a5 5 0 0110 0v3M6 11h12a1 1 0 011 1v7a1 1 0 01-1 1H6a1 1 0 01-1-1v-7a1 1 0 011-1z" />
    </svg>
  ),
  eye: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12S5.5 5.5 12 5.5 21.5 12 21.5 12 18.5 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  eyeOff: (
    <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18M10.6 10.6a3 3 0 004.2 4.2M9.9 5.7A9.3 9.3 0 0112 5.5c6.5 0 9.5 6.5 9.5 6.5a16 16 0 01-2.9 3.9M6.2 6.9A16 16 0 002.5 12S5.5 18.5 12 18.5a9 9 0 003.3-.6" />
    </svg>
  ),
};

export default AuthModal;
